import { forwardRef, type HTMLAttributes, type ReactNode } from "react";
import { X } from "lucide-react";
import { cn } from "@/lib/cn";

// Stage 5: local replacement for @datasirpi/ui's <Chip>. Same DOM shape,
// same interaction/ARIA model as Chip-XNKHQMQU.css's implementation
// (read directly from the vendor bundle, not guessed) — variant colors are
// its own semantic palette (--ds-color-success/warning/danger/info), a
// DIFFERENT thing from this app's Stage 3 [data-tone] system (which is
// driven by numeric risk/posture score bands). Conflating the two would
// silently recolor every plain-status chip in the app, so this keeps them
// separate and reads --ds-* directly, exactly like the vendor CSS did —
// verified those values aren't reachable through this app's own --d2d-*
// layer (different palette, different values in at least one theme).
// Dropped the dark-mode neon glow — already force-disabled repo-wide, see
// Card.tsx's header for the existing override this predates.
export type ChipSize = "sm" | "md";
/* Phase 1.5 — `brand` is gone, replaced by `neutral`.
   The variant was already RENDERING neutral (globals.css stopped filling it
   with orange when the accent was rationed), but it still called itself
   "neutral", so every call site read as if it were deliberately spending the
   accent on data — the one thing config.css's role rule forbids. A name that
   contradicts what it draws is worse than either, because the next person
   trusts the name. Same value, honest label. */
export type ChipVariant = "default" | "success" | "warning" | "danger" | "info" | "neutral";

export interface ChipProps extends Omit<HTMLAttributes<HTMLSpanElement>, "onClick"> {
  children: ReactNode;
  icon?: ReactNode;
  size?: ChipSize;
  variant?: ChipVariant;
  onClick?: () => void;
  pressed?: boolean;
  disabled?: boolean;
  onRemove?: () => void;
  removeLabel?: string;
}

export const Chip = forwardRef<HTMLSpanElement, ChipProps>(function Chip(
  {
    className,
    children,
    icon,
    size = "sm",
    variant = "default",
    onClick,
    pressed,
    disabled,
    onRemove,
    removeLabel = "Remove",
    ...rest
  },
  ref
) {
  const interactive = Boolean(onClick);
  // d2d-DetectHub eval F16: a chip that is both clickable and removable used
  // to be role="button" with the remove <button> inside it, which nests one
  // control in another. Then the label is its own <button> beside the remove
  // button, and the chip itself is a plain container.
  const split = interactive && Boolean(onRemove);
  const interactiveProps = interactive && !split
    ? {
        role: "button" as const,
        tabIndex: disabled ? -1 : 0,
        "aria-pressed": pressed,
        "aria-disabled": disabled || undefined,
        onClick: disabled ? undefined : onClick,
        onKeyDown: disabled
          ? undefined
          : (e: React.KeyboardEvent) => {
              if (e.key === "Enter" || e.key === " ") {
                e.preventDefault();
                onClick?.();
              }
            },
      }
    : {};

  return (
    <span
      ref={ref}
      data-size={size}
      data-variant={variant}
      data-interactive={interactive || undefined}
      data-pressed={interactive && pressed ? true : undefined}
      data-disabled={disabled || undefined}
      className={cn("rc-chip", className)}
      {...interactiveProps}
      {...rest}
    >
      {icon ? <span className="rc-chip__icon">{icon}</span> : null}
      {split ? (
        <button type="button" className="rc-chip__main" aria-pressed={pressed} disabled={disabled} onClick={() => onClick?.()}>
          {children}
        </button>
      ) : (
        <span>{children}</span>
      )}
      {onRemove ? (
        <button
          type="button"
          aria-label={removeLabel}
          disabled={disabled}
          className="rc-chip__remove"
          onClick={(e) => {
            e.stopPropagation();
            onRemove();
          }}
        >
          <X width={12} height={12} />
        </button>
      ) : null}
    </span>
  );
});
